import { useEffect, useState } from "react";
import axios from "axios";
import { X, CreditCard, Loader2, ExternalLink, Globe, Landmark, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { fmt as fmtMoney } from "@/lib/currency";
import BankTransferModal from "./BankTransferModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

export default function PayInvoiceModal({ invoice, user, onClose, onPaid }) {
    const [config, setConfig] = useState(null);
    const [tabbyStatus, setTabbyStatus] = useState({ configured: false });
    const [cbqStatus, setCbqStatus] = useState({ configured: false });
    const [busy, setBusy] = useState(null); // 'rzp' | 'stripe' | null
    const [bankOpen, setBankOpen] = useState(false);

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/payments/config`).catch(() => ({ data: { razorpay: { enabled: false }, stripe: { enabled: false } } })),
            axios.get(`${API}/payments/tabby/status`).catch(() => ({ data: { configured: false } })),
            axios.get(`${API}/payments/cbq/status`).catch(() => ({ data: { configured: false } })),
        ]).then(([c, tb, cb]) => {
            setConfig(c.data);
            setTabbyStatus(tb.data);
            setCbqStatus(cb.data);
        });
    }, []);

    const payRazorpay = async () => {
        setBusy("rzp");
        try {
            const ok = await loadRazorpayScript();
            if (!ok) throw new Error("Razorpay SDK failed to load");
            const { data: order } = await axios.post(`${API}/invoices/${invoice.id}/razorpay-order`);
            const rzp = new window.Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                name: "Toothrocket Labs",
                description: `Invoice ${order.invoice_number}`,
                order_id: order.order_id,
                prefill: { name: user?.clinic_name || "", email: user?.email || "" },
                theme: { color: "#0B1120" },
                handler: async (resp) => {
                    try {
                        await axios.post(`${API}/payments/razorpay/verify`, resp);
                        toast.success("Payment received");
                        onPaid?.();
                        onClose();
                    } catch (e) {
                        toast.error("Payment verification failed");
                    }
                },
                modal: { ondismiss: () => setBusy(null) },
            });
            rzp.open();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e.message || "Razorpay error");
            setBusy(null);
        }
    };

    const payStripe = async () => {
        setBusy("stripe");
        try {
            const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
                invoice_id: invoice.id,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Stripe error");
            setBusy(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-5" data-testid="pay-invoice-modal">
            {bankOpen && (
                <BankTransferModal
                    amountQar={invoice.total}
                    invoiceId={invoice.id}
                    onClose={() => setBankOpen(false)}
                    onConfirmed={() => { onPaid?.(); }}
                />
            )}
            <div className="bg-white rounded-2xl w-full max-w-md p-7 relative max-h-[80vh] overflow-y-auto tr-modal-card">
                <button onClick={onClose} className="absolute top-4 end-4 text-slate-400 hover:text-slate-700"><X size={18} /></button>
                <div className="tr-overline mb-2">· Pay invoice</div>
                <div className="font-mono-tr text-lg text-slate-900 tracking-wider">{invoice.invoice_number}</div>
                <div className="mt-3 text-[14px] text-slate-600">Amount due</div>
                <div className="font-display text-5xl text-slate-900 tracking-tight">{fmtMoney(invoice.total, invoice.currency)}</div>

                <div className="mt-7 space-y-3">
                    {!config && (
                        <div className="text-center text-slate-500 py-4"><Loader2 className="inline animate-spin" /></div>
                    )}

                    {/* 1. BANK TRANSFER — preferred */}
                    {config && (
                        <button onClick={() => setBankOpen(true)} disabled={busy !== null} data-testid="pay-bank-btn"
                            className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-60 relative">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center"><Landmark size={18} /></div>
                                <div className="text-start">
                                    <div className="font-semibold text-[15px] flex items-center gap-2">Bank transfer
                                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-900 text-emerald-50 text-[9.5px] font-bold tracking-wide uppercase">Preferred</span>
                                    </div>
                                    <div className="text-[12px] text-white/70">Commercial Bank of Qatar · IBAN provided</div>
                                </div>
                            </div>
                            <ExternalLink size={16} className="text-white/70" />
                        </button>
                    )}

                    {/* 2. CBQ CARD GATEWAY — placeholder until keys filled */}
                    {config && (
                        cbqStatus.configured ? (
                            <button disabled data-testid="pay-cbq-btn"
                                className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-[#0B1120] text-white hover:bg-slate-800 transition disabled:opacity-60">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-lg bg-white/10 flex items-center justify-center"><CreditCard size={18} /></div>
                                    <div className="text-start">
                                        <div className="font-semibold text-[15px]">CBQ Card Gateway</div>
                                        <div className="text-[12px] text-white/60">Visa · Mastercard via Commercial Bank of Qatar</div>
                                    </div>
                                </div>
                                <ExternalLink size={16} className="text-white/60" />
                            </button>
                        ) : (
                            <DisabledOption testid="pay-cbq-btn" icon={CreditCard} title="CBQ Card Gateway" subtitle="Coming soon · Commercial Bank of Qatar (admin to wire keys)" />
                        )
                    )}

                    {/* 3. STRIPE — international fallback */}
                    {config?.stripe?.enabled ? (
                        <button onClick={payStripe} disabled={busy !== null} data-testid="pay-stripe-btn" className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-white border border-slate-200 hover:border-cyan-300 transition disabled:opacity-60">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center"><Globe size={18} /></div>
                                <div className="text-start">
                                    <div className="font-semibold text-[15px] text-slate-900">Stripe Checkout</div>
                                    <div className="text-[12px] text-slate-500">Visa · Mastercard · Amex · QAR / USD / AED / INR</div>
                                </div>
                            </div>
                            {busy === "stripe" ? <Loader2 className="animate-spin" size={18} /> : <ExternalLink size={16} className="text-slate-400" />}
                        </button>
                    ) : (
                        <DisabledOption testid="pay-stripe-btn" icon={Globe} title="Stripe" subtitle="Admin hasn't added Stripe keys yet" />
                    )}

                    {/* 4. RAZORPAY — India only */}
                    {config?.razorpay?.enabled ? (
                        <button onClick={payRazorpay} disabled={busy !== null} data-testid="pay-razorpay-btn" className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-white border border-slate-200 hover:border-cyan-300 transition disabled:opacity-60">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center"><CreditCard size={18} /></div>
                                <div className="text-start">
                                    <div className="font-semibold text-[15px] text-slate-900">Razorpay</div>
                                    <div className="text-[12px] text-slate-500">UPI · Cards · Netbanking (India)</div>
                                </div>
                            </div>
                            {busy === "rzp" ? <Loader2 className="animate-spin" size={18} /> : <ExternalLink size={16} className="text-slate-400" />}
                        </button>
                    ) : (
                        <DisabledOption testid="pay-razorpay-btn" icon={CreditCard} title="Razorpay" subtitle="Admin hasn't added Razorpay keys yet" />
                    )}

                    {/* 5. TABBY BNPL — placeholder */}
                    {config && (
                        tabbyStatus.configured ? (
                            <button disabled data-testid="pay-tabby-btn"
                                className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-[#3FE9C3] text-slate-900 hover:bg-[#34d4af] transition disabled:opacity-60">
                                <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-lg bg-white/40 flex items-center justify-center"><Sparkles size={18} /></div>
                                    <div className="text-start">
                                        <div className="font-semibold text-[15px]">Tabby</div>
                                        <div className="text-[12px] text-slate-700">Split into 4 interest-free payments</div>
                                    </div>
                                </div>
                                <ExternalLink size={16} />
                            </button>
                        ) : (
                            <DisabledOption testid="pay-tabby-btn" icon={Sparkles} title="Tabby BNPL" subtitle="Coming soon · split into 4 interest-free installments" />
                        )
                    )}
                </div>

                <div className="mt-6 pt-5 border-t border-slate-100 text-[12px] text-slate-500">
                    Secured by the payment provider. We never see your card details.
                </div>
            </div>
        </div>
    );
}

function DisabledOption({ icon: Icon, title, subtitle, testid }) {
    return (
        <div data-testid={testid} aria-disabled="true" className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 opacity-70">
            <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Icon size={18} /></div>
                <div className="text-start">
                    <div className="font-semibold text-[15px]">{title}</div>
                    <div className="text-[12px]">{subtitle}</div>
                </div>
            </div>
        </div>
    );
}
